"use client";

import { useMemo } from "react";
import { Calendar, User, ArrowRight, ArrowLeft, Clock } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import type { Post } from "@/integrations/wordpress/types";
import { isDuplicatePost } from "@/lib/posts";

declare const wvcClient: any;

function PostHeader() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 w-3/4 rounded-md bg-muted animate-pulse" />
        <div className="h-6 w-1/2 rounded-md bg-muted animate-pulse" />
        <div className="aspect-[16/9] w-full rounded-lg bg-muted animate-pulse" />
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="font-default text-muted-foreground">Article not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const dateFormatted = post.date
    ? new Date(post.date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="space-y-8">
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {post.categories.map((cat, i) => {
            const hasLink = Boolean(cat.link && typeof cat.link === "string" && cat.link.trim() !== "");
            const badgeClasses = "font-default inline-flex items-center rounded-md bg-accent px-3 py-1 text-xs font-medium uppercase tracking-wider text-accent-foreground transition-colors hover:bg-accent/80";
            return hasLink ? (
              <Link
                key={cat.id}
                data-index={i}
                href={cat.link}
                className={badgeClasses}
              >
                {cat.name}
              </Link>
            ) : cat.slug ? (
              <Link
                key={cat.id}
                data-index={i}
                to={`/InsightsPage?category=${encodeURIComponent(cat.slug)}`}
                className={badgeClasses}
              >
                {cat.name}
              </Link>
            ) : (
              <Link
                key={cat.id}
                data-index={i}
                to="/InsightsPage"
                className={badgeClasses}
              >
                {cat.name}
              </Link>
            );
          })}
        </div>
      )}

      <h1
        className="font-default text-4xl font-semibold leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
        {post.author && (
          <div className="flex items-center gap-2">
            <Avatar className="size-8">
              {post.author.avatar && post.author.avatar["48"] && (
                <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
              )}
              <AvatarFallback className="font-default text-xs">
                {post.author.name?.charAt(0) || "A"}
              </AvatarFallback>
            </Avatar>
            <span className="font-default">By {post.author.name}</span>
          </div>
        )}
        {dateFormatted && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4" />
            <span className="font-default">{dateFormatted}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Clock className="size-4" />
          <span className="font-default">5 min read</span>
        </div>
      </div>

      {post.featuredImage && (
        <div className="overflow-hidden rounded-lg shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="aspect-[16/9] w-full object-cover"
          />
        </div>
      )}

      {post.excerpt && (
        <div
          className="font-default border-l-2 border-secondary pl-6 text-lg italic leading-relaxed text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {post.content && (
        <div
          className="font-default prose prose-lg max-w-none text-foreground [&_a]:text-secondary [&_a]:underline [&_h2]:font-default [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h3]:font-default [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-2xl [&_h3]:font-semibold [&_p]:mb-5 [&_p]:leading-relaxed [&_ul]:my-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-5 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-2 [&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-secondary [&_blockquote]:pl-6 [&_blockquote]:italic [&_img]:my-6 [&_img]:rounded-lg"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {stripHtml(post.title).toLowerCase().includes("audit") && (
        <p className="font-default mt-6 text-base leading-relaxed text-muted-foreground">
          Want to see the results? Read our{" "}
          <Link
            href="https://hotelrevbaba.com/case-studies/"
            className="text-secondary underline transition-colors hover:text-secondary/80"
          >
            verified hotel revenue turnaround case studies
          </Link>
          .
        </p>
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-6">
          <span className="font-default text-sm font-medium text-foreground">Tags:</span>
          {post.tags.map((tag, i) => {
            const hasTagLink = Boolean(tag.link && typeof tag.link === "string" && tag.link.trim() !== "");
            const tagClasses = "font-default rounded-md border border-border bg-card px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground";
            return hasTagLink ? (
              <Link
                key={tag.id}
                data-index={i}
                href={tag.link}
                className={tagClasses}
              >
                #{tag.name}
              </Link>
            ) : (
              <Link
                key={tag.id}
                data-index={i}
                to="/InsightsPage"
                className={tagClasses}
              >
                #{tag.name}
              </Link>
            );
          })}
        </div>
      )}
    </article>
  );
}

function PostNeighboursNavContent({ currentPostId }: { currentPostId: string | number }) {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 pt-4 sm:grid-cols-2">
        <div className="h-20 rounded-lg bg-muted animate-pulse" />
        <div className="h-20 rounded-lg bg-muted animate-pulse" />
      </div>
    );
  }

  if (!posts || posts.length === 0) return null;

  // Filter out duplicate posts from navigation pool
  const filteredPosts = posts.filter((p) => !isDuplicatePost(p));

  const currentIdStr = String(currentPostId);
  const currentIndex = filteredPosts.findIndex((p) => String(p.id) === currentIdStr);

  // If current post is not found in the filtered pool (e.g. current post is a duplicate),
  // do not render prev/next navigation
  if (currentIndex === -1) return null;

  // posts are ordered by date DESC: index 0 is newest
  // Next newer post is index - 1 (published later)
  // Previous older post is index + 1 (published earlier)
  const nextPost = currentIndex > 0 ? filteredPosts[currentIndex - 1] : null;
  const prevPost = currentIndex < filteredPosts.length - 1 ? filteredPosts[currentIndex + 1] : null;

  if (!nextPost && !prevPost) return null;

  return (
    <nav
      aria-label="Previous and Next Post"
      className="grid grid-cols-1 gap-6 pt-4 sm:grid-cols-2"
    >
      {prevPost && prevPost.link ? (
        <div className="flex flex-col items-start text-left">
          <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            « Previous Post
          </span>
          <Link
            href={prevPost.link}
            className="group mt-1 inline-flex items-center gap-2 text-base font-semibold text-foreground transition-colors hover:text-secondary"
          >
            <ArrowLeft className="size-4 shrink-0 transition-transform group-hover:-translate-x-1" />
            <span dangerouslySetInnerHTML={{ __html: prevPost.title }} />
          </Link>
        </div>
      ) : (
        <div />
      )}

      {nextPost && nextPost.link ? (
        <div className="flex flex-col items-end text-right sm:col-start-2">
          <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
            Next Post »
          </span>
          <Link
            href={nextPost.link}
            className="group mt-1 inline-flex items-center justify-end gap-2 text-base font-semibold text-foreground transition-colors hover:text-secondary"
          >
            <span dangerouslySetInnerHTML={{ __html: nextPost.title }} />
            <ArrowRight className="size-4 shrink-0 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      ) : null}
    </nav>
  );
}

function PostNeighboursNav({ currentPostId }: { currentPostId: string | number }) {
  const allChronologicalQuery = useMemo(
    () =>
      new WP_Query({
        post_type: "post",
        per_page: 100,
        orderby: "date",
        order: "DESC",
      }),
    []
  );

  return (
    <PostsProvider wp_query={allChronologicalQuery}>
      <PostNeighboursNavContent currentPostId={currentPostId} />
    </PostsProvider>
  );
}

function AuthorBio() {
  const { posts, loading } = usePosts();

  if (loading || !posts || posts.length === 0) {
    return null;
  }

  const author = posts[0].author;
  if (!author) return null;

  return (
    <Card className="bg-card shadow-md">
      <CardContent className="flex flex-col items-start gap-6 px-8 py-8 md:flex-row md:items-center">
        <Avatar className="size-20 shrink-0">
          {author.avatar && author.avatar["96"] && (
            <AvatarImage src={author.avatar["96"]} alt={author.name} />
          )}
          <AvatarFallback className="font-default text-2xl">
            {author.name?.charAt(0) || "A"}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
            <User className="size-3" />
            <span className="font-default">Expert Consultant</span>
          </div>
          <h3 className="font-default text-2xl font-semibold text-foreground">
            {author.name}
          </h3>
          <p className="font-default text-sm leading-relaxed text-muted-foreground">
            Sunil Singh is a seasoned revenue management expert dedicated to helping hotels optimize their OTA performance, implement cutting-edge tech stacks, and drive sustainable profitability through data-driven strategies.
          </p>
          <Link
            to="/InsightsPage"
            className="font-default inline-flex items-center gap-1 pt-2 text-sm font-medium text-secondary hover:underline"
          >
            View all insights <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function ConversionCTA() {
  return (
    <div className="rounded-lg bg-primary px-8 py-12 text-center shadow-xl">
      <div className="mx-auto max-w-2xl space-y-6">
        <h2 className="font-default text-3xl font-bold tracking-tight text-primary-foreground md:text-4xl">
          Ready to Optimize Your Revenue?
        </h2>
        <p className="font-default text-lg text-primary-foreground/80">
          Take the first step towards maximizing your property's potential with a professional revenue audit or a personalized consultation.
        </p>
        <div className="flex flex-wrap justify-center gap-4 pt-4">
          <Link to="/ContactPage">
            <Button size="lg" variant="secondary" className="font-default font-semibold">
              Book a Consultation
            </Button>
          </Link>
          <Link to="/ServicesPage">
            <Button size="lg" variant="outline" className="font-default border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
              Explore Services
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function stripHtml(html?: string) {
  if (!html) return "";
  return html.replace(/<[^>]+>/g, "").trim();
}

function calculateReadTime(content?: string, excerpt?: string): string | null {
  const textToAnalyze = content || excerpt;
  if (!textToAnalyze) return null;

  const plainText = stripHtml(textToAnalyze);
  const words = plainText.match(/\S+/g);
  const wordCount = words ? words.length : 0;

  if (wordCount === 0) {
    return content ? "1 min read" : null;
  }

  const minutes = Math.max(1, Math.ceil(wordCount / 200));
  return `${minutes} min read`;
}

function RelatedCardsGrid({ posts }: { posts: Post[] }) {
  if (posts.length === 0) {
    return (
      <p className="font-default text-center text-muted-foreground">
        No related articles found.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
        const dateFormatted = post.date
          ? new Date(post.date).toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        const hasPostLink = Boolean(post.link && typeof post.link === "string" && post.link.trim() !== "");
        const cardInner = (
          <Card className="h-full overflow-hidden bg-card shadow-md transition-shadow hover:shadow-lg">
            {post.featuredImage && (
              <div className="overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            )}
            <CardContent className="space-y-3 px-6">
              {post.categories && post.categories.length > 0 && (
                <span className="font-default inline-block text-xs uppercase tracking-wider text-secondary">
                  {post.categories[0].name}
                </span>
              )}
              <h4
                className="font-default text-xl font-semibold leading-snug tracking-tight text-foreground transition-colors group-hover:text-secondary"
                dangerouslySetInnerHTML={{ __html: post.title }}
              />
              {post.excerpt && stripHtml(post.excerpt) && (
                <p className="font-default text-sm leading-relaxed text-muted-foreground line-clamp-3">
                  {stripHtml(post.excerpt)}
                </p>
              )}
              {(dateFormatted || calculateReadTime(post.content, post.excerpt)) && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {dateFormatted && (
                    <div className="flex items-center gap-2">
                      <Calendar className="size-3.5" />
                      <span className="font-default">{dateFormatted}</span>
                    </div>
                  )}
                  {calculateReadTime(post.content, post.excerpt) && (
                    <div className="flex items-center gap-2">
                      <Clock className="size-3.5" />
                      <span className="font-default">{calculateReadTime(post.content, post.excerpt)}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="font-default inline-flex items-center gap-1 pt-2 text-sm font-medium text-secondary">
                Read more
                <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
              </div>
            </CardContent>
          </Card>
        );

        return hasPostLink ? (
          <Link
            key={post.id}
            data-index={i}
            href={post.link}
            className="group block"
          >
            {cardInner}
          </Link>
        ) : (
          <Link
            key={post.id}
            data-index={i}
            to="/InsightsPage"
            className="group block"
          >
            {cardInner}
          </Link>
        );
      })}
    </div>
  );
}

function RelatedPostsList({
  currentPostId,
  categoryIds,
  currentPostDate,
}: {
  currentPostId: string | number;
  categoryIds: number[];
  currentPostDate?: string;
}) {
  const { posts, loading } = usePosts();

  if (loading) {
    return <RelatedPostsSkeleton />;
  }

  // Exclude current post and all duplicate posts
  const otherPosts = (posts || []).filter(
    (p) => String(p.id) !== String(currentPostId) && !isDuplicatePost(p)
  );

  // Current post timestamp for proximity tie-break
  const currentTimestamp = currentPostDate ? new Date(currentPostDate).getTime() : NaN;
  const hasValidCurrentTime = !isNaN(currentTimestamp);

  // Score posts by category overlap with current post
  // Priority category ids (245, 246, 247, 248) weigh 3, other matches weigh 1
  const priorityCategoryIds = new Set([245, 246, 247, 248]);
  const currentCategorySet = new Set(categoryIds);

  const scoredPosts = otherPosts.map((p) => {
    let score = 0;
    if (p.categories && p.categories.length > 0) {
      for (const cat of p.categories) {
        if (currentCategorySet.has(cat.id)) {
          score += priorityCategoryIds.has(cat.id) ? 3 : 1;
        }
      }
    }

    const postTimestamp = p.date ? new Date(p.date).getTime() : NaN;
    const timeDiff = hasValidCurrentTime && !isNaN(postTimestamp)
      ? Math.abs(currentTimestamp - postTimestamp)
      : Number.MAX_SAFE_INTEGER;

    return { post: p, score, timeDiff };
  });

  // Ranking:
  // 1. Category-overlap score DESC
  // 2. Tie-break among equal scores: post whose date is closest to current post's date (timeDiff ASC)
  // 3. Fallback if identical timeDiff: newer post first
  scoredPosts.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    if (a.timeDiff !== b.timeDiff) {
      return a.timeDiff - b.timeDiff;
    }
    const dateA = a.post.date ? new Date(a.post.date).getTime() : 0;
    const dateB = b.post.date ? new Date(b.post.date).getTime() : 0;
    return dateB - dateA;
  });

  const selectedPosts = scoredPosts.slice(0, 6).map((item) => item.post);

  return <RelatedCardsGrid posts={selectedPosts} />;
}

function RelatedPostsSection({
  currentPostId,
  categoryIds,
  currentPostDate,
}: {
  currentPostId: string | number;
  categoryIds: number[];
  currentPostDate?: string;
}) {
  const queryKey = useMemo(
    () => `related-${currentPostId}-${categoryIds.join("-")}`,
    [currentPostId, categoryIds]
  );

  // Fetch candidate pool of ALL posts (per_page: 100), excluding current post via post__not_in
  const numericCurrentId = Number(currentPostId);
  const excludeArray = !isNaN(numericCurrentId) && numericCurrentId > 0 ? [numericCurrentId] : [];

  const candidateQuery = useMemo(
    () =>
      new WP_Query({
        post_type: "post",
        per_page: 100,
        orderby: "date",
        order: "DESC",
        ...(excludeArray.length > 0 ? { post__not_in: excludeArray } : {}),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPostId]
  );

  return (
    <PostsProvider key={queryKey} wp_query={candidateQuery}>
      <RelatedPostsList
        currentPostId={currentPostId}
        categoryIds={categoryIds}
        currentPostDate={currentPostDate}
      />
    </PostsProvider>
  );
}

function RelatedPostsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="space-y-3">
          <div className="aspect-[4/3] w-full rounded-lg bg-muted animate-pulse" />
          <div className="h-5 w-3/4 rounded-md bg-muted animate-pulse" />
          <div className="h-4 w-1/2 rounded-md bg-muted animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function MainPostContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <>
        <PostHeader />
        <Separator className="my-16" />
        <div className="space-y-6">
          <div className="h-32 rounded-lg bg-muted animate-pulse" />
        </div>
        <Separator className="my-16" />
        <div className="space-y-16">
          <ConversionCTA />
          <Separator className="my-16" />
          <div className="space-y-10">
            <div className="space-y-3 text-center">
              <span className="font-mono text-xs uppercase tracking-widest text-secondary">
                Continue Reading
              </span>
              <h3 className="font-default text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
                Related Revenue Management &amp; Distribution Insights
              </h3>
              <p className="font-default mx-auto max-w-2xl text-muted-foreground">
                Explore more expert strategies and hospitality trends to maximize your property's revenue potential.
              </p>
            </div>
            <RelatedPostsSkeleton />
          </div>
        </div>
      </>
    );
  }

  if (!posts || posts.length === 0) {
    return <PostHeader />;
  }

  const currentPost = posts[0];
  const categoryIds = (currentPost.categories || []).map((c) => c.id);

  return (
    <>
      <PostHeader />

      <div className="mt-12">
        <PostNeighboursNav currentPostId={currentPost.id} />
      </div>

      <Separator className="my-16" />

      <AuthorBio />

      <Separator className="my-16" />

      <div className="space-y-16">
        <ConversionCTA />

        <Separator className="my-16" />

        <div className="space-y-10">
          <div className="space-y-3 text-center">
            <span className="font-mono text-xs uppercase tracking-widest text-secondary">
              Continue Reading
            </span>
            <h3 className="font-default text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
              Related Revenue Management &amp; Distribution Insights
            </h3>
            <p className="font-default mx-auto max-w-2xl text-muted-foreground">
              Explore more expert strategies and hospitality trends to maximize your property's revenue potential.
            </p>
          </div>

          <RelatedPostsSection
            currentPostId={currentPost.id}
            categoryIds={categoryIds}
            currentPostDate={currentPost.date}
          />

          <div className="flex justify-center pt-4">
            <Link to="/InsightsPage">
              <Button
                variant="outline"
                size="lg"
                className="font-default border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground"
              >
                View All Insights
                <ArrowRight className="ml-2 size-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };
  const wp_query = useMemo(
    () => new WP_Query(currentWPQueryParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(currentWPQueryParams)]
  );

  return (
    <section
      data-section-id="3164"
      id="single-post-template-section"
      className="bg-background py-16 md:py-24"
    >
      <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <PostsProvider wp_query={wp_query}>
          <MainPostContent />
        </PostsProvider>
      </div>
    </section>
  );
}
